import React, { useEffect, useRef, useState } from 'react';
import AuthService from '../../common/services/AuthService';
import DeepLinkService from '../../routing/services/DeepLinkService';
import TrackingService from '../../tracking/services/TrackingService';
import AlertsService from '../services/AlertsService';
import RequireAuthentication from '../components/RequireAuthentication';
import { ErrorBoundary } from 'react-error-boundary';
import { AppErrorPage } from '../components/AppErrorPage';
import { UrlRestorer } from './UrlRestorer';
import { AppMainPage } from './AppMainPage';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { AuthContextProvider } from '@contexts/auth-context';
import { ValueExpressionContextProvider } from '@contexts/value-expression-context';
import AlertsContextProvider from '@contexts/alerts-context/alerts.context';
import { DeepLinkContextProvider } from '@contexts/deep-link-context';
import { OrgTreeContextProvider } from '@contexts/org-tree-context';
import { useComponentWillMount } from '@hooks/use-component-will-mount-hook';

const ONE_MINUTE = 1000 * 60;
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * ONE_MINUTE,
      cacheTime: 10 * ONE_MINUTE,
      refetchOnWindowFocus: false,
    },
  },
});

const AppRoot: React.FC = () => {
  const trackingService = useRef<TrackingService>(null);
  const authService = useRef<AuthService>(null);
  const deepLinkService = useRef<DeepLinkService>(null);
  const alertsService = useRef<AlertsService>(null);

  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    return () => {
      deepLinkService.current.destruct();
      alertsService.current.destruct();
    };
  }, []);

  const setup = async () => {
    trackingService.current = new TrackingService();
    authService.current = new AuthService(trackingService.current);
    deepLinkService.current = new DeepLinkService(authService.current, trackingService.current);
    alertsService.current = new AlertsService(authService.current);
    const authServicePromise = authService.current.init();
    const deepLinkPromise = deepLinkService.current.init();

    await Promise.all([deepLinkPromise, authServicePromise]);
  };

  useComponentWillMount(async () => {
    try {
      await setup();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error on initializing', e);
    } finally {
      setInitializing(false);
    }
  });

  if (initializing) {
    return <span>Init</span>;
  }

  return (
    <ErrorBoundary
      FallbackComponent={AppErrorPage}
      onReset={() => {
        window.location.href = '/';
      }}>
      <QueryClientProvider client={queryClient}>
        <AuthContextProvider authService={authService.current}>
          <AlertsContextProvider alertsService={alertsService.current}>
            <RequireAuthentication>
              <DeepLinkContextProvider deepLinkService={deepLinkService.current}>
                <UrlRestorer>
                  <OrgTreeContextProvider>
                    <ValueExpressionContextProvider>
                      <ReactQueryDevtools />
                      <AppMainPage />
                    </ValueExpressionContextProvider>
                  </OrgTreeContextProvider>
                </UrlRestorer>
              </DeepLinkContextProvider>
            </RequireAuthentication>
          </AlertsContextProvider>
        </AuthContextProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default AppRoot;
